// Geometry for lawn_zones.boundary_geojson: a GeoJSON Polygon with one outer
// ring and optional interior rings (holes: driveways, beds, patios). Pure and
// dependency-free so the API route, the plan engine and root jest can share it.
//
// Positions are GeoJSON [lng, lat]. Areas use the same local equirectangular
// shoelace as the mobile measure tool (mobile/lib/lawn-measure-geometry.ts),
// with every ring projected at the outer ring's mean latitude, so the server's
// numbers match what the app shows.

export type Position = [number, number]

/** Interior rings allowed per zone boundary. */
export const MAX_ZONE_HOLES = 12

/**
 * Slack for comparing a client-reported area with the server's computed
 * outer-ring area: 2% plus 1 sq ft covers rounding and older clients' slightly
 * different projection.
 */
const AREA_TOLERANCE_RATIO = 1.02
const AREA_TOLERANCE_SQFT = 1

const EARTH_RADIUS_M = 6_378_137
const M2_PER_FT2 = 0.092_903_04

function isPosition(value: unknown): value is Position {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number' &&
    Number.isFinite(value[0]) &&
    Number.isFinite(value[1])
  )
}

/** The rings of a GeoJSON Polygon (outer first), or null when it is not one. */
export function polygonRings(geojson: unknown): Position[][] | null {
  if (!geojson || typeof geojson !== 'object') return null
  const g = geojson as { type?: unknown; coordinates?: unknown }
  if (g.type !== 'Polygon' || !Array.isArray(g.coordinates) || g.coordinates.length === 0) return null
  return g.coordinates.map((ring) =>
    Array.isArray(ring) ? ring.filter(isPosition).map((p) => [p[0], p[1]] as Position) : [],
  )
}

function samePosition(a: Position, b: Position): boolean {
  return a[0] === b[0] && a[1] === b[1]
}

/** Drops the repeated closing position. */
function openRing(ring: Position[]): Position[] {
  if (ring.length > 1 && samePosition(ring[0], ring[ring.length - 1])) return ring.slice(0, -1)
  return ring
}

function meanLatitude(points: Position[]): number {
  return points.reduce((sum, p) => sum + p[1], 0) / points.length
}

/** Area of one ring in square feet, projected at `refLat` (defaults to the ring's own mean latitude). */
export function ringAreaSqFt(ring: Position[], refLat?: number): number {
  const open = openRing(ring)
  if (open.length < 3) return 0
  const cos = Math.cos(((refLat ?? meanLatitude(open)) * Math.PI) / 180)
  const origin = open[0]
  const projected = open.map((p) => ({
    x: (((p[0] - origin[0]) * Math.PI) / 180) * EARTH_RADIUS_M * cos,
    y: (((p[1] - origin[1]) * Math.PI) / 180) * EARTH_RADIUS_M,
  }))
  let area2 = 0
  for (let i = 0; i < projected.length; i += 1) {
    const a = projected[i]
    const b = projected[(i + 1) % projected.length]
    area2 += a.x * b.y - b.x * a.y
  }
  return Math.abs(area2) / 2 / M2_PER_FT2
}

export interface BoundaryAreas {
  grossSqft: number
  holesSqft: number
  netSqft: number
  holeCount: number
}

/** Outer minus holes, floored at 0 (same rule as computeNetArea in lib/lawn-size-calculator.ts, kept local so this module stays import-free). */
export function computeNetArea(grossSqFt: number, excludedSqFt: number): number {
  return Math.max(0, grossSqFt - excludedSqFt)
}

/** Outer, hole and net (outer minus holes, floored at 0) areas of a boundary, unrounded. */
export function boundaryAreas(geojson: unknown): BoundaryAreas | null {
  const rings = polygonRings(geojson)
  if (!rings) return null
  const outer = openRing(rings[0])
  if (outer.length < 3) return null
  const refLat = meanLatitude(outer)
  const grossSqft = ringAreaSqFt(outer, refLat)
  const holes = rings.slice(1).filter((ring) => openRing(ring).length >= 3)
  const holesSqft = holes.reduce((sum, hole) => sum + ringAreaSqFt(hole, refLat), 0)
  return { grossSqft, holesSqft, netSqft: computeNetArea(grossSqft, holesSqft), holeCount: holes.length }
}

/** Net boundary area rounded to whole square feet, or null without a usable boundary. */
export function boundaryNetAreaSqft(geojson: unknown): number | null {
  const areas = boundaryAreas(geojson)
  return areas ? Math.round(areas.netSqft) : null
}

/**
 * The area value a zone contributes to lawn size and amounts, in the raw form
 * the size parsers take. A manual override always wins. Without one, a
 * boundary with holes contributes its computed net area; everything else keeps
 * using area_sqft unchanged.
 */
export function zoneAreaInput<T>(zone: {
  area_override_sqft?: T | null
  area_sqft?: T | null
  boundary_geojson?: unknown
}): T | number | null {
  if (zone.area_override_sqft != null && (zone.area_override_sqft as unknown) !== '') return zone.area_override_sqft
  const areas = boundaryAreas(zone.boundary_geojson)
  if (areas && areas.holeCount > 0) return Math.round(areas.netSqft)
  return zone.area_sqft ?? null
}

/** Whether a client-reported area fits inside the boundary's outer ring (with rounding slack). */
export function areaFitsOuterRing(areaSqft: number, geojson: unknown): boolean {
  const areas = boundaryAreas(geojson)
  if (!areas) return true
  return areaSqft <= areas.grossSqft * AREA_TOLERANCE_RATIO + AREA_TOLERANCE_SQFT
}

// ---- Validation ---------------------------------------------------------------

function orientation(p: Position, q: Position, r: Position): number {
  return (q[0] - p[0]) * (r[1] - q[1]) - (q[1] - p[1]) * (r[0] - q[0])
}

function onSegment(p: Position, q: Position, r: Position): boolean {
  return (
    q[0] <= Math.max(p[0], r[0]) &&
    q[0] >= Math.min(p[0], r[0]) &&
    q[1] <= Math.max(p[1], r[1]) &&
    q[1] >= Math.min(p[1], r[1])
  )
}

/** Whether two segments cross or touch. */
export function segmentsIntersect(p1: Position, p2: Position, p3: Position, p4: Position): boolean {
  const o1 = orientation(p1, p2, p3)
  const o2 = orientation(p1, p2, p4)
  const o3 = orientation(p3, p4, p1)
  const o4 = orientation(p3, p4, p2)

  if (o1 > 0 !== o2 > 0 && o3 > 0 !== o4 > 0 && o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0) {
    return true
  }

  if (o1 === 0 && onSegment(p1, p3, p2)) return true
  if (o2 === 0 && onSegment(p1, p4, p2)) return true
  if (o3 === 0 && onSegment(p3, p1, p4)) return true
  if (o4 === 0 && onSegment(p3, p2, p4)) return true
  return false
}

/** Whether a closed ring's edges cross each other (adjacent edges excluded). */
export function ringSelfIntersects(ring: Position[]): boolean {
  const points = openRing(ring)
  const n = points.length
  if (n < 4) return false

  for (let i = 0; i < n; i++) {
    const a1 = points[i]
    const a2 = points[(i + 1) % n]
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue
      const b1 = points[j]
      const b2 = points[(j + 1) % n]
      if (segmentsIntersect(a1, a2, b1, b2)) return true
    }
  }
  return false
}

function ringsTouch(a: Position[], b: Position[]): boolean {
  const pa = openRing(a)
  const pb = openRing(b)
  for (let i = 0; i < pa.length; i++) {
    const a1 = pa[i]
    const a2 = pa[(i + 1) % pa.length]
    for (let j = 0; j < pb.length; j++) {
      if (segmentsIntersect(a1, a2, pb[j], pb[(j + 1) % pb.length])) return true
    }
  }
  return false
}

/** Ray-casting point-in-ring test. Points exactly on an edge are unspecified; callers check edges separately. */
function pointInRing(point: Position, ring: Position[]): boolean {
  const pts = openRing(ring)
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i]
    const [xj, yj] = pts[j]
    if (yi > point[1] !== yj > point[1] && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

function ringShapeError(ring: Position[], label: string): string | null {
  if (ring.length < 4) return `${label} must include at least 3 points and a closing point`
  if (!samePosition(ring[0], ring[ring.length - 1])) return `${label} Polygon ring must be closed`
  const uniquePoints = new Set(ring.slice(0, -1).map(([lng, lat]) => `${lng},${lat}`))
  if (uniquePoints.size < 3) return `${label} must include at least 3 unique points`
  if (ringSelfIntersects(ring)) return `${label} cannot cross itself`
  return null
}

/**
 * Validates Polygon rings: an outer ring plus up to MAX_ZONE_HOLES holes. Every
 * ring is closed, has 3+ unique points and does not cross itself; every hole
 * lies strictly inside the outer ring (no shared edges or corners) and holes
 * neither touch nor contain each other. Returns the first problem, or null.
 */
export function validatePolygonRings(rings: Position[][]): string | null {
  if (rings.length === 0) return 'Boundary coordinates are required'
  if (rings.length - 1 > MAX_ZONE_HOLES) return `Boundary can include at most ${MAX_ZONE_HOLES} cut-outs`

  const outerError = ringShapeError(rings[0], 'Boundary')
  if (outerError) return outerError

  const outer = rings[0]
  const holes = rings.slice(1)
  for (let i = 0; i < holes.length; i++) {
    const holeError = ringShapeError(holes[i], 'Cut-out')
    if (holeError) return holeError
    if (ringsTouch(holes[i], outer) || !pointInRing(holes[i][0], outer)) {
      return 'Cut-out must be inside the boundary'
    }
  }
  for (let i = 0; i < holes.length; i++) {
    for (let j = i + 1; j < holes.length; j++) {
      if (ringsTouch(holes[i], holes[j]) || pointInRing(holes[i][0], holes[j]) || pointInRing(holes[j][0], holes[i])) {
        return 'Cut-outs cannot overlap'
      }
    }
  }
  return null
}
